import { Component, Input, OnInit, OnChanges } from '@angular/core';
import {
    ParsedImportFile,
    ImportConfiguration,
    CleanedDomainData,
} from '../../../../types/domain-management.types';
import { DomainDataType } from 'types/domain';

@Component({
    selector: 'mon-import-wizard-confirm',
    templateUrl: './confirm.component.html',
    styleUrls: ['./confirm.component.scss'],
    standalone: false,
})
export class ImportWizardConfirmComponent implements OnInit, OnChanges {
    @Input() parsedFile: ParsedImportFile | null = null;
    @Input() configuration: ImportConfiguration | null = null;

    currentPage = 1;
    pageSize = 10;
    totalPages = 0;
    paginatedDomains: CleanedDomainData[] = [];

    Math = Math;

    ngOnInit (): void {
        this.updatePagination();
    }

    ngOnChanges (): void {
        this.updatePagination();
    }

    /**
     * Update pagination when data changes
     */
    private updatePagination (): void {
        if (!this.parsedFile?.domains) {
            this.paginatedDomains = [];
            this.totalPages = 0;
            return;
        }

        this.totalPages = Math.ceil(this.parsedFile.domains.length / this.pageSize);
        const startIndex = (this.currentPage - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        this.paginatedDomains = this.parsedFile.domains.slice(startIndex, endIndex);
    }

    /**
     * Navigate to specific page
     */
    goToPage (page: number): void {
        if (page >= 1 && page <= this.totalPages) {
            this.currentPage = page;
            this.updatePagination();
        }
    }

    /**
     * Go to next page
     */
    nextPage (): void {
        this.goToPage(this.currentPage + 1);
    }

    /**
     * Go to previous page
     */
    previousPage (): void {
        this.goToPage(this.currentPage - 1);
    }

    /**
     * Get page numbers for pagination display
     */
    getPageNumbers (): number[] {
        const pages: number[] = [];
        const maxVisible = 5;
        let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
        const end = Math.min(this.totalPages, start + maxVisible - 1);

        if (end - start + 1 < maxVisible) {
            start = Math.max(1, end - maxVisible + 1);
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        return pages;
    }

    /**
     * Get friendly label for configuration option
     */
    getConfigLabel (key: keyof ImportConfiguration): string {
        switch (key) {
            case 'skipDomainGroups':
                return 'Domain Groups';
            case 'skipPathConstraints':
                return 'Path Constraints';
            case 'skipLinkExcludes':
                return 'Link Excludes';
            default:
                return key;
        }
    }

    /**
     * Get status text for configuration option
     */
    getConfigStatus (key: keyof ImportConfiguration): string {
        if (!this.configuration) {
            return 'Include';
        }

        return this.configuration[key] ? 'Skip' : 'Include';
    }

    /**
     * Get CSS class for configuration status
     */
    getConfigStatusClass (key: keyof ImportConfiguration): string {
        if (!this.configuration) {
            return 'text-success';
        }

        return this.configuration[key] ? 'text-muted' : 'text-success';
    }

    /**
     * Get icon for configuration option
     */
    getConfigIcon (key: keyof ImportConfiguration): string {
        switch (key) {
            case 'skipDomainGroups':
                return 'fas fa-layer-group';
            case 'skipPathConstraints':
                return 'fas fa-route';
            case 'skipLinkExcludes':
                return 'fas fa-ban';
            default:
                return 'fas fa-cog';
        }
    }

    /**
     * Check if domain has specific component
     */
    hasDomainComponent (domain: CleanedDomainData, component: string): boolean {
        const typedDomain = domain as DomainDataType;
        switch (component) {
            case 'groups':
                return !!(typedDomain.domain_groups?.length);
            case 'constraints':
                return !!(typedDomain.path_constraints?.length);
            case 'excludes':
                return !!(typedDomain.link_excludes?.length);
            default:
                return false;
        }
    }
}
